import {
  Poster,
  TvAlertKey,
  TvAlertMinutes,
  TvDailySettings,
} from "types/TvPosterTypes";

/** Valgene for «Tid per plakat», i sekunder. */
export const SLIDE_SECONDS = [5, 8, 10, 15, 20, 30, 45, 60];
export const DEFAULT_SLIDE_SECONDS = 15;

/** Valgene, pluss en lagret verdi som ikke er i listen (5–120 er lov). */
export const slideOptions = (current: number) =>
  SLIDE_SECONDS.includes(current)
    ? SLIDE_SECONDS
    : [...SLIDE_SECONDS, current].sort((a, b) => a - b);

/** Gyldig `slideSeconds` fra backend, ellers standard (15). */
export const normalizeSeconds = (value: unknown) =>
  typeof value === "number" && value >= 5 && value <= 120
    ? Math.round(value)
    : DEFAULT_SLIDE_SECONDS;

/** «Varselskjermer på TV»: standard = slik TV-ene har gjort det til nå. */
export const DEFAULT_ALERT_MINUTES: TvAlertMinutes = {
  adhanMinutes: 5,
  iqamahMinutes: 10,
  jummahMinutes: 15,
};
export const MIN_ALERT_MINUTES = 1;
export const MAX_ALERT_MINUTES = 30;

/** Valgene for varselskjermene: 1–30 minutter. */
export const ALERT_MINUTE_OPTIONS = Array.from(
  { length: MAX_ALERT_MINUTES - MIN_ALERT_MINUTES + 1 },
  (_, i) => MIN_ALERT_MINUTES + i,
);

/** Feltene i rekkefølge, med navnet admin ser. */
export const ALERT_FIELDS: Array<{ key: TvAlertKey; label: string }> = [
  { key: "adhanMinutes", label: "Adhan" },
  { key: "iqamahMinutes", label: "Fellesbønn (iqamah)" },
  { key: "jummahMinutes", label: "Jummah" },
];

/** Gyldige minutter (1–30) fra backend; ellers standard for feltet. */
export const normalizeAlertMinutes = (
  settings: Partial<Record<TvAlertKey, unknown>> | null | undefined,
): TvAlertMinutes => {
  const pick = (key: TvAlertKey) => {
    const value = settings?.[key];
    return typeof value === "number" &&
      value >= MIN_ALERT_MINUTES &&
      value <= MAX_ALERT_MINUTES
      ? Math.round(value)
      : DEFAULT_ALERT_MINUTES[key];
  };
  return {
    adhanMinutes: pick("adhanMinutes"),
    iqamahMinutes: pick("iqamahMinutes"),
    jummahMinutes: pick("jummahMinutes"),
  };
};

/** Dagens vers og hadith: av som standard; 60 s per plakat i fullskjerm. */
export const DEFAULT_DAILY_SETTINGS: TvDailySettings = {
  showDailyHadith: false,
  showDailyQuran: false,
  dailyBreakMinutes: 0,
  dailyBreakSeconds: 60,
};

const withCurrent = (list: number[], current: number) =>
  list.includes(current) ? list : [...list, current].sort((a, b) => a - b);

/** «Hvor ofte» (minutter), pluss en lagret verdi som ikke er i listen. */
export const DAILY_BREAK_MINUTE_OPTIONS = (current: number) =>
  withCurrent([5, 10, 15, 20, 30, 45, 60, 90, 120], current);

/** «Tid per plakat» i fullskjerm (sekunder). */
export const DAILY_BREAK_SECOND_OPTIONS = (current: number) =>
  withCurrent([15, 30, 45, 60, 90, 120, 180, 300], current);

/** Gyldige verdier fra backend; ellers standard (eldre servere mangler feltene). */
export const normalizeDailySettings = (
  settings: Partial<Record<keyof TvDailySettings, unknown>> | null | undefined,
): TvDailySettings => {
  const minutes = settings?.dailyBreakMinutes;
  const seconds = settings?.dailyBreakSeconds;
  return {
    showDailyHadith: settings?.showDailyHadith === true,
    showDailyQuran: settings?.showDailyQuran === true,
    dailyBreakMinutes:
      typeof minutes === "number" && minutes >= 5 && minutes <= 120
        ? Math.round(minutes)
        : 0,
    dailyBreakSeconds:
      typeof seconds === "number" && seconds >= 15 && seconds <= 300
        ? Math.round(seconds)
        : DEFAULT_DAILY_SETTINGS.dailyBreakSeconds,
  };
};

/** Prioritet først (lavest øverst), så id, som TV-en sorterer. */
const byPriority = (a: Poster, b: Poster) =>
  (a.priority ?? 0) - (b.priority ?? 0) || a.id - b.id;

/** Plakatene TV-en viser («Vis på TV»), i rekkefølgen den viser dem. */
export const tvOrder = (posters: Poster[]) =>
  posters.filter((p) => p.activatedForTv).sort(byPriority);

/** Flytter elementet fra `from` til `to` (ny liste). */
export const reorder = <T>(list: T[], from: number, to: number): T[] => {
  if (
    from === to ||
    from < 0 ||
    from >= list.length ||
    to < 0 ||
    to >= list.length
  )
    return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

/**
 * Hele id-rekkefølgen som sendes til `PUT …/posters/order`: TV-plakatene i ny
 * rekkefølge, så resten i dagens rekkefølge.
 */
export const fullOrder = (posters: Poster[], tvIds: number[]) => {
  const rest = posters
    .filter((p) => !tvIds.includes(p.id))
    .sort(byPriority)
    .map((p) => p.id);
  return [...tvIds, ...rest];
};

/** Gir plakatene prioritet 1, 2, 3 … etter `ids` (som backend gjør). */
export const applyOrder = (posters: Poster[], ids: number[]): Poster[] => {
  const rank = new Map(ids.map((id, i) => [id, i + 1]));
  let next = ids.length;
  return posters.map((p) => ({
    ...p,
    priority: rank.get(p.id) ?? ++next,
  }));
};

/** Bildet til karusellen (omtrent kvadratisk plass): stående når den finnes. */
export const carouselImage = (poster: Poster) =>
  poster.plakatPortrait || poster.plakat;
